import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { transactionsApi, categoriesApi, exportApi, importApi, simplefinApi, merchantPatternsApi, MerchantPattern } from '@/lib/api'
import { formatCurrency, formatDate, downloadBlob, cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Trash2, Search, Pencil, Download, Filter, X, Upload, FileSpreadsheet, Receipt, PlusCircle, Wand2 } from 'lucide-react'
import { CategoryIcon } from '@/lib/category-icon'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Transaction, Category, TAX_CATEGORIES, TaxCategory } from '@/types'
import LinkToBillDialog from '@/components/LinkToBillDialog'
import FinanceAppImportDialog from '@/components/FinanceAppImportDialog'

interface TransactionResponse {
  transactions: Transaction[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

export default function Transactions() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [minAmount, setMinAmount] = useState('')
  const [maxAmount, setMaxAmount] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 50
  const [linkingTransaction, setLinkingTransaction] = useState<Transaction | null>(null)
  const [isCreatingCategory, setIsCreatingCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryType, setNewCategoryType] = useState<'expense' | 'bill' | 'income'>('expense')
  const [editCategoryId, setEditCategoryId] = useState<string>('')
  const [editTaxDeductible, setEditTaxDeductible] = useState(false)
  const [editTaxCategory, setEditTaxCategory] = useState<string>('')
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<{
    success?: boolean
    imported?: number
    skipped?: number
    total?: number
    errors?: string[]
  } | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isFinanceImportOpen, setIsFinanceImportOpen] = useState(false)
  const [newTransactionType, setNewTransactionType] = useState<'expense' | 'income'>('expense')
  const [taxFilter, setTaxFilter] = useState(false)
  const [accountFilter, setAccountFilter] = useState<string>('')

  // "Create pattern from merchant" dialog state
  const [patternTransaction, setPatternTransaction] = useState<Transaction | null>(null)
  const [patternForm, setPatternForm] = useState<{ pattern: string; category_id: string; min_amount: string; max_amount: string }>({
    pattern: '',
    category_id: '',
    min_amount: '',
    max_amount: '',
  })
  const [patternFormError, setPatternFormError] = useState<string | null>(null)
  const [createdPattern, setCreatedPattern] = useState<MerchantPattern | null>(null)
  const [patternBackfillMessage, setPatternBackfillMessage] = useState<string | null>(null)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Read filters from URL params on mount
  useEffect(() => {
    const type = searchParams.get('type')
    if (type === 'income' || type === 'expense') {
      setTypeFilter(type)
    }
    const category = searchParams.get('category_id')
    if (category) {
      setCategoryFilter(category)
    }
  }, [searchParams])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, categoryFilter, typeFilter, startDate, endDate, minAmount, maxAmount, taxFilter, accountFilter])

  const { data, isLoading } = useQuery<TransactionResponse>({
    queryKey: ['transactions', categoryFilter, typeFilter, debouncedSearch, startDate, endDate, minAmount, maxAmount, page, taxFilter, accountFilter],
    queryFn: async () => {
      const params: any = {
        limit: pageSize,
        offset: page * pageSize
      }
      if (categoryFilter === 'uncategorized') {
        params.uncategorized = true
      } else if (categoryFilter) {
        params.category_id = parseInt(categoryFilter)
      }
      if (typeFilter !== 'all') {
        params.type = typeFilter
      }
      if (debouncedSearch) {
        params.search = debouncedSearch
      }
      if (startDate) {
        params.start_date = startDate
      }
      if (endDate) {
        params.end_date = endDate
      }
      if (minAmount) {
        params.min_amount = parseFloat(minAmount)
      }
      if (maxAmount) {
        params.max_amount = parseFloat(maxAmount)
      }
      if (taxFilter) {
        params.tax_deductible = true
      }
      if (accountFilter) {
        params.bank_account_id = parseInt(accountFilter)
      }
      const res = await transactionsApi.getAll(params)
      return res.data
    },
  })

  const transactions = data?.transactions || []
  const pagination = data?.pagination

  // Get all categories for the filter dropdown (expense only)
  const { data: expenseCategories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'expense'],
    queryFn: async () => {
      const res = await categoriesApi.getAll('expense')
      return res.data
    },
  })

  // Get ALL categories for editing (both bill and expense)
  const { data: allCategories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'all'],
    queryFn: async () => {
      const res = await categoriesApi.getAll()
      return res.data
    },
  })

  // Get income categories for create form
  const { data: incomeCategories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'income'],
    queryFn: async () => {
      const res = await categoriesApi.getAll('income')
      return res.data
    },
  })

  // Get bank accounts for filter dropdown
  const { data: bankAccounts = [] } = useQuery<{ id: number; name: string; institution: string }[]>({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const res = await simplefinApi.getAccounts()
      return res.data
    },
  })

  const createTransaction = useMutation({
    mutationFn: transactionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
      setIsCreateOpen(false)
    },
  })

  const updateTransactionMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<import('@/lib/api').CreateTransactionData> }) =>
      transactionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
      setEditingTransaction(null)
    },
  })

  const deleteTransaction = useMutation({
    mutationFn: transactionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
    },
  })

  const createPatternMutation = useMutation({
    mutationFn: (data: { category_id: number; pattern: string; min_amount?: number | null; max_amount?: number | null }) =>
      merchantPatternsApi.create(data),
    onSuccess: (newPattern) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-patterns'] })
      setCreatedPattern(newPattern)
      setPatternFormError(null)
    },
    onError: (err: any) => {
      setPatternFormError(err?.response?.data?.error || 'Failed to create pattern')
    },
  })

  const backfillPatternMutation = useMutation({
    mutationFn: (id: number) => merchantPatternsApi.backfill(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
      setPatternBackfillMessage(`Applied to ${data.updated} transaction${data.updated === 1 ? '' : 's'}.`)
    },
    onError: (err: any) => {
      setPatternFormError(err?.response?.data?.error || 'Failed to apply pattern')
    },
  })

  function openPatternDialog(t: Transaction) {
    setPatternTransaction(t)
    setPatternForm({
      pattern: t.merchant || '',
      category_id: t.category_id != null ? String(t.category_id) : '',
      min_amount: '',
      max_amount: '',
    })
    setPatternFormError(null)
    setCreatedPattern(null)
    setPatternBackfillMessage(null)
  }

  function closePatternDialog() {
    setPatternTransaction(null)
    setCreatedPattern(null)
    setPatternFormError(null)
    setPatternBackfillMessage(null)
  }

  function handlePatternSave() {
    setPatternFormError(null)
    if (!patternForm.pattern.trim()) {
      setPatternFormError('Pattern is required')
      return
    }
    if (!patternForm.category_id) {
      setPatternFormError('Category is required')
      return
    }
    const min = patternForm.min_amount.trim() === '' ? null : Number(patternForm.min_amount)
    const max = patternForm.max_amount.trim() === '' ? null : Number(patternForm.max_amount)
    if (min != null && Number.isNaN(min)) {
      setPatternFormError('Min amount must be a number')
      return
    }
    if (max != null && Number.isNaN(max)) {
      setPatternFormError('Max amount must be a number')
      return
    }
    if (min != null && max != null && min > max) {
      setPatternFormError('Min amount cannot be greater than max')
      return
    }
    createPatternMutation.mutate({
      pattern: patternForm.pattern.trim(),
      category_id: parseInt(patternForm.category_id),
      min_amount: min,
      max_amount: max,
    })
  }

  const createCategory = useMutation({
    mutationFn: (data: { name: string; type: 'bill' | 'expense' | 'income' }) =>
      categoriesApi.create({ name: data.name, type: data.type }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      const newId = res.data.id.toString()
      setEditCategoryId(newId)
      setIsCreatingCategory(false)
      setNewCategoryName('')
    },
  })

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    createTransaction.mutate({
      merchant: formData.get('merchant') as string,
      amount: parseFloat(formData.get('amount') as string),
      date: formData.get('date') as string,
      type: newTransactionType,
      category_id: formData.get('category_id') ? parseInt(formData.get('category_id') as string) : undefined,
      notes: formData.get('notes') as string || undefined,
    })
  }

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editingTransaction) return
    const categoryId = editCategoryId && editCategoryId !== 'none' ? parseInt(editCategoryId) : undefined
    const selectedCat = categoryId ? allCategories.find(c => c.id === categoryId) : undefined
    const inferredType = selectedCat?.type === 'income' ? 'income' : selectedCat ? 'expense' : undefined
    updateTransactionMutation.mutate({
      id: editingTransaction.id,
      data: {
        category_id: categoryId,
        ...(inferredType ? { type: inferredType } : {}),
        tax_deductible: editTaxDeductible,
        tax_category: editTaxDeductible ? (editTaxCategory as TaxCategory || null) : null,
      },
    })
  }

  const hasActiveFilters = debouncedSearch || categoryFilter || typeFilter !== 'all' ||
    startDate || endDate || minAmount || maxAmount || taxFilter || accountFilter

  const clearAllFilters = () => {
    setSearchTerm('')
    setCategoryFilter('')
    setTypeFilter('all')
    setStartDate('')
    setEndDate('')
    setMinAmount('')
    setMaxAmount('')
    setTaxFilter(false)
    setAccountFilter('')
    searchParams.delete('type')
    searchParams.delete('category_id')
    setSearchParams(searchParams)
  }

  const groupedByDate = transactions.reduce((acc, t) => {
    const date = t.date.split('T')[0]
    if (!acc[date]) acc[date] = []
    acc[date].push(t)
    return acc
  }, {} as Record<string, Transaction[]>)

  const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  )

  // Group categories by type for the edit dropdown
  const billCategories = allCategories.filter(c => c.type === 'bill')
  const expenseCats = allCategories.filter(c => c.type === 'expense')
  const incomeCats = allCategories.filter(c => c.type === 'income')

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.transactionsCsv({
        startDate: searchParams.get('startDate') || undefined,
        endDate: searchParams.get('endDate') || undefined,
        categoryId: searchParams.get('category_id') ? parseInt(searchParams.get('category_id')!) : undefined
      })
      downloadBlob(response.data, `transactions-${new Date().toISOString().split('T')[0]}.csv`)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const handleImportCsv = async () => {
    if (!importFile) return

    setIsImporting(true)
    setImportResult(null)

    try {
      const response = await importApi.transactionsCsv(importFile)
      setImportResult(response.data)
      if (response.data.success && response.data.imported > 0) {
        queryClient.invalidateQueries({ queryKey: ['transactions'] })
        queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
      }
    } catch (error: any) {
      setImportResult({
        success: false,
        errors: [error.response?.data?.error || 'Import failed']
      })
    } finally {
      setIsImporting(false)
    }
  }

  const handleCloseImport = () => {
    setIsImportOpen(false)
    setImportFile(null)
    setImportResult(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {typeFilter === 'income' ? 'Income' : typeFilter === 'expense' ? 'Expenses' : 'Transactions'}
          </h1>
          <p className="text-muted-foreground">
            {typeFilter === 'income' ? 'Money coming in' : typeFilter === 'expense' ? 'Money going out' : 'Track your spending'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Import Transactions</DialogTitle>
                <DialogDescription>
                  Upload a CSV file to import transactions. The file should have columns for date, merchant/description, and amount.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <FileSpreadsheet className="mx-auto h-10 w-10 text-muted-foreground mb-2" />
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      setImportFile(e.target.files?.[0] || null)
                      setImportResult(null)
                    }}
                    className="max-w-xs mx-auto"
                  />
                  {importFile && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>

                {importResult && (
                  <Alert variant={importResult.success ? "default" : "destructive"}>
                    <AlertDescription>
                      {importResult.success ? (
                        <div>
                          <p className="font-medium">Import complete!</p>
                          <p className="text-sm">
                            Imported: {importResult.imported} | Skipped: {importResult.skipped} | Total: {importResult.total}
                          </p>
                          {importResult.errors && importResult.errors.length > 0 && (
                            <div className="mt-2 text-xs">
                              <p className="font-medium">Errors:</p>
                              {importResult.errors.slice(0, 5).map((err, i) => (
                                <p key={i}>{err}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium">Import failed</p>
                          {importResult.errors?.map((err, i) => (
                            <p key={i} className="text-sm">{err}</p>
                          ))}
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseImport}>
                  {importResult?.success ? 'Close' : 'Cancel'}
                </Button>
                {!importResult?.success && (
                  <Button
                    onClick={handleImportCsv}
                    disabled={!importFile || isImporting}
                  >
                    {isImporting ? 'Importing...' : 'Import'}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={() => setIsFinanceImportOpen(true)}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Import from App
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) setNewTransactionType('expense') }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Transaction
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Transaction</DialogTitle>
              <DialogDescription>
                Record a new transaction. Auto-categorization will attempt to match the merchant.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={newTransactionType} onValueChange={(v: 'expense' | 'income') => setNewTransactionType(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="merchant">Merchant</Label>
                <Input
                  id="merchant"
                  name="merchant"
                  placeholder="e.g., Walmart, Amazon"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    step="0.01"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    defaultValue={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category_id">Category (optional)</Label>
                <Select name="category_id">
                  <SelectTrigger>
                    <SelectValue placeholder="Auto-detect or select" />
                  </SelectTrigger>
                  <SelectContent>
                    {newTransactionType === 'income'
                      ? incomeCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))
                      : expenseCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" name="notes" placeholder="Optional" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createTransaction.isPending}>
                  {createTransaction.isPending ? 'Adding...' : 'Add Transaction'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editingTransaction} onOpenChange={(open) => {
        if (!open) {
          setEditingTransaction(null)
          setIsCreatingCategory(false)
          setNewCategoryName('')
          setEditCategoryId('')
          setEditTaxDeductible(false)
          setEditTaxCategory('')
        } else if (editingTransaction) {
          setEditCategoryId(editingTransaction.category_id?.toString() || 'none')
          setEditTaxDeductible(!!editingTransaction.tax_deductible)
          setEditTaxCategory(editingTransaction.tax_category || '')
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogDescription>
              Change the category for "{editingTransaction?.merchant}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Amount</Label>
              <p className="text-lg font-semibold">{editingTransaction && formatCurrency(editingTransaction.amount)}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_category_id">Category</Label>
              <Select
                value={editCategoryId || (editingTransaction?.category_id?.toString() || 'none')}
                onValueChange={(v) => setEditCategoryId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorized</SelectItem>
                  {incomeCats.length > 0 && (
                    <>
                      <SelectItem value="income-header" disabled className="font-semibold text-xs uppercase text-muted-foreground">
                        Income
                      </SelectItem>
                      {incomeCats.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {expenseCats.length > 0 && (
                    <>
                      <SelectItem value="expense-header" disabled className="font-semibold text-xs uppercase text-muted-foreground">
                        Expenses
                      </SelectItem>
                      {expenseCats.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {billCategories.length > 0 && (
                    <>
                      <SelectItem value="bill-header" disabled className="font-semibold text-xs uppercase text-muted-foreground">
                        Bills
                      </SelectItem>
                      {billCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>

              {!isCreatingCategory ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => setIsCreatingCategory(true)}
                >
                  <PlusCircle className="h-3 w-3 mr-1" />
                  Add new category
                </Button>
              ) : (
                <div className="rounded-md border p-3 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="new_cat_name" className="text-xs">New Category Name</Label>
                    <Input
                      id="new_cat_name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="e.g., Pet Supplies"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Type</Label>
                    <Select value={newCategoryType} onValueChange={(v: 'expense' | 'bill' | 'income') => setNewCategoryType(v)}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="bill">Bill</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => {
                        setIsCreatingCategory(false)
                        setNewCategoryName('')
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="text-xs"
                      disabled={!newCategoryName.trim() || createCategory.isPending}
                      onClick={() => createCategory.mutate({ name: newCategoryName.trim(), type: newCategoryType })}
                    >
                      {createCategory.isPending ? 'Adding...' : 'Add Category'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Tax Deductible</Label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setEditTaxDeductible(!editTaxDeductible)}
                  className={cn(
                    'text-sm px-3 py-1 rounded-full border transition-colors',
                    editTaxDeductible
                      ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-muted text-muted-foreground border-transparent hover:border-border'
                  )}
                >
                  {editTaxDeductible ? '★ Tax Deductible' : '☆ Not Tax Deductible'}
                </button>
              </div>
              {editTaxDeductible && (
                <div className="space-y-1">
                  <Label htmlFor="edit_tax_category" className="text-xs">Tax Category</Label>
                  <Select
                    value={editTaxCategory || 'none'}
                    onValueChange={(v) => setEditTaxCategory(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tax category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {TAX_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingTransaction(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateTransactionMutation.isPending}>
                {updateTransactionMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="space-y-4">
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search merchants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={typeFilter}
            onValueChange={(v: 'all' | 'income' | 'expense') => {
              setTypeFilter(v)
              if (v === 'all') {
                searchParams.delete('type')
              } else {
                searchParams.set('type', v)
              }
              setSearchParams(searchParams)
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All transactions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All transactions</SelectItem>
              <SelectItem value="income">Income only</SelectItem>
              <SelectItem value="expense">Expenses only</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={categoryFilter || "all"}
            onValueChange={(v) => {
              setCategoryFilter(v === "all" ? "" : v)
              if (v === "all") {
                searchParams.delete('category_id')
              } else {
                searchParams.set('category_id', v)
              }
              setSearchParams(searchParams)
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              {expenseCats.length > 0 && (
                <>
                  <SelectItem value="expense-header" disabled className="font-semibold text-xs uppercase text-muted-foreground">
                    Expenses
                  </SelectItem>
                  {expenseCats.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </>
              )}
              {billCategories.length > 0 && (
                <>
                  <SelectItem value="bill-header" disabled className="font-semibold text-xs uppercase text-muted-foreground">
                    Bills
                  </SelectItem>
                  {billCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          {bankAccounts.length > 0 && (
            <Select
              value={accountFilter || "all"}
              onValueChange={(v) => setAccountFilter(v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {bankAccounts.map((acct) => (
                  <SelectItem key={acct.id} value={acct.id.toString()}>
                    {acct.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant={showAdvancedFilters ? "secondary" : "outline"}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            More Filters
          </Button>
          <Button
            variant={taxFilter ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTaxFilter(!taxFilter)}
            className="flex items-center gap-2"
          >
            <Receipt className="h-4 w-4" />
            Tax Deductible Only
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearAllFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min Amount</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Amount</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results summary */}
        {pagination && (
          <p className="text-sm text-muted-foreground">
            Showing {transactions.length} of {pagination.total} transactions
            {hasActiveFilters && " (filtered)"}
          </p>
        )}
      </div>

      {/* Transaction List */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : sortedDates.length === 0 ? (
          <p className="text-muted-foreground">No transactions found</p>
        ) : (
          sortedDates.map((date) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                {formatDate(date)}
              </h3>
              <div className="space-y-2">
                {groupedByDate[date].map((transaction) => (
                  <Card key={transaction.id} className="hover:bg-accent/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div
                          className="flex items-center gap-3 flex-1 cursor-pointer"
                          onClick={() => setEditingTransaction(transaction)}
                        >
                          <div
                            className="h-10 w-10 rounded-full flex items-center justify-center text-white font-medium"
                            style={{
                              backgroundColor: transaction.category_color || '#6b7280',
                            }}
                          >
                            {transaction.merchant.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{transaction.merchant}</p>
                            {transaction.category_name ? (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs cursor-pointer hover:opacity-80"
                                style={{
                                  backgroundColor: `${transaction.category_color}20`,
                                  color: transaction.category_color || undefined,
                                }}
                              >
                                {transaction.category_name}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                Uncategorized - click to set
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            'font-semibold',
                            transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                          )}>
                            {transaction.type === 'income' ? '+' : '−'}{formatCurrency(Math.abs(transaction.amount))}
                          </span>
                          <button
                            title={transaction.tax_deductible ? 'Tax deductible — click to remove' : 'Mark as tax deductible'}
                            onClick={() => updateTransactionMutation.mutate({
                              id: transaction.id,
                              data: { tax_deductible: !transaction.tax_deductible }
                            })}
                            className={cn(
                              'text-xs px-2 py-0.5 rounded-full border transition-colors',
                              transaction.tax_deductible
                                ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-muted text-muted-foreground border-transparent hover:border-border'
                            )}
                          >
                            {transaction.tax_deductible ? '★ Tax' : '☆ Tax'}
                          </button>
                          {transaction.amount < 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setLinkingTransaction(transaction)}
                              title={transaction.linked_bill_name ? `Linked to: ${transaction.linked_bill_name}` : 'Link to bill'}
                            >
                              <Receipt className={`h-4 w-4 ${transaction.linked_bill_id ? 'text-green-600' : 'text-muted-foreground'}`} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPatternDialog(transaction)}
                            title="Create pattern from this merchant"
                          >
                            <Wand2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingTransaction(transaction)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteTransaction.mutate(transaction.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Pattern from Merchant Dialog */}
      <Dialog
        open={!!patternTransaction}
        onOpenChange={(open) => {
          if (!open) closePatternDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Pattern</DialogTitle>
            <DialogDescription>
              Auto-categorize future transactions whose description matches (case-insensitive
              substring). Optional amount bounds scope the pattern to specific dollar ranges.
            </DialogDescription>
          </DialogHeader>

          {!createdPattern ? (
            <>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pattern-merchant">Pattern</Label>
                  <Input
                    id="pattern-merchant"
                    placeholder="e.g. STARBUCKS"
                    value={patternForm.pattern}
                    onChange={(e) => setPatternForm({ ...patternForm, pattern: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pattern-category">Category</Label>
                  <Select
                    value={patternForm.category_id}
                    onValueChange={(value) => setPatternForm({ ...patternForm, category_id: value })}
                  >
                    <SelectTrigger id="pattern-category">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCategories.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          <span className="inline-flex items-center gap-1.5">
                            <CategoryIcon name={c.icon} />
                            {c.name}
                            <span className="text-muted-foreground text-xs ml-1">({c.type})</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="pattern-min">Min Amount</Label>
                    <Input
                      id="pattern-min"
                      type="number"
                      step="0.01"
                      placeholder="any"
                      value={patternForm.min_amount}
                      onChange={(e) => setPatternForm({ ...patternForm, min_amount: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pattern-max">Max Amount</Label>
                    <Input
                      id="pattern-max"
                      type="number"
                      step="0.01"
                      placeholder="any"
                      value={patternForm.max_amount}
                      onChange={(e) => setPatternForm({ ...patternForm, max_amount: e.target.value })}
                    />
                  </div>
                </div>

                {patternFormError && (
                  <Alert variant="destructive">
                    <AlertDescription>{patternFormError}</AlertDescription>
                  </Alert>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closePatternDialog}>
                  Cancel
                </Button>
                <Button onClick={handlePatternSave} disabled={createPatternMutation.isPending}>
                  {createPatternMutation.isPending ? 'Saving...' : 'Create Pattern'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3 py-2">
                {!patternBackfillMessage ? (
                  <Alert>
                    <AlertDescription>
                      Pattern created. Apply it to existing uncategorized transactions now?
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <AlertDescription>{patternBackfillMessage}</AlertDescription>
                  </Alert>
                )}
                {patternFormError && (
                  <Alert variant="destructive">
                    <AlertDescription>{patternFormError}</AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                {!patternBackfillMessage ? (
                  <>
                    <Button variant="outline" onClick={closePatternDialog}>
                      No
                    </Button>
                    <Button
                      onClick={() => backfillPatternMutation.mutate(createdPattern.id)}
                      disabled={backfillPatternMutation.isPending}
                    >
                      {backfillPatternMutation.isPending ? 'Applying...' : 'Yes, apply'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={closePatternDialog}>Done</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Link to Bill Dialog */}
      <LinkToBillDialog
        transaction={linkingTransaction}
        open={!!linkingTransaction}
        onOpenChange={(open) => !open && setLinkingTransaction(null)}
      />

      <FinanceAppImportDialog
        open={isFinanceImportOpen}
        onOpenChange={setIsFinanceImportOpen}
      />

      {/* Pagination */}
      {pagination && pagination.total > pageSize && (
        <div className="flex items-center justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {Math.ceil(pagination.total / pageSize)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={!pagination.hasMore}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
